import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { db } from '../config/firebase';
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  limit, 
  onSnapshot,
  DocumentData,
  QueryDocumentSnapshot,
  QuerySnapshot,
  DocumentSnapshot,
  Timestamp,
  updateDoc,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import type { User } from 'firebase/auth';

interface LeaderboardEntry {
  userId: string;
  username: string;
  avatar: string;
  totalWins: number;
  totalLosses: number;
  winRate: number;
  totalEarnings: number;
  rank: number;
}

interface ChallengeResult {
  challengeId: string;
  winner: string;
  loser: string;
  amount: number;
  timestamp: string;
  details: {
    winnerScore: number;
    loserScore: number;
    duration: string;
    questionsAnswered: number;
    accuracy: number;
  };
}

interface CompetitionRound {
  id: string;
  creatorId: string;
  stakeAmount: number;
  startTime: Date;
  competitionMode: 'VOICE' | 'TEXT';
  competitionType: 'MCQ' | 'WRITTEN' | 'QUICKFIRE';
  category: string;
  subCategory: string;
  module: string;
  subModule: string;
  status: 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  opponentId?: string;
  result?: 'WIN' | 'LOSS' | 'DRAW';
  createdAt: Date;
  updatedAt: Date;
}

interface UserStats {
  totalRounds: number;
  wins: number;
  losses: number;
  draws: number;
  rating: number;
  yellowCards: number;
  redCards: number;
  cardExpiry?: Date;
}

interface CompetitionContextType {
  getBalance: () => Promise<number>;
  getActiveChallenges: () => Promise<any[]>;
  getLeaderboard: () => Promise<LeaderboardEntry[]>;
  getChallengeResult: (challengeId: string) => Promise<ChallengeResult>;
  subscribeToChallengeUpdates: (challengeId: string, callback: (data: any) => void) => () => void;
  subscribeToLeaderboardUpdates: (callback: (data: LeaderboardEntry[]) => void) => () => void;
  withdrawReward: (amount: number) => Promise<void>;
  createCompetitionRound: (round: Omit<CompetitionRound, 'id' | 'creatorId' | 'status' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  acceptCompetitionRound: (roundId: string) => Promise<void>;
  cancelCompetitionRound: (roundId: string, reason: string) => Promise<void>;
  getUserStats: (userId: string) => Promise<UserStats>;
  getAvailableCompetitors: (page: number) => Promise<{ data: any[]; hasMore: boolean }>;
  currentCompetition: {
    id: string;
    rewards: {
      available: number;
      withdrawn: number;
    };
  } | null;
  state: {
    balance: number;
    stats: UserStats;
    availableCompetitors: Array<{
      id: number;
      name: string;
      balance: number;
      wins: number;
      rating: number;
      status: string;
      avatar: string;
      accountType: 'student' | 'public';
      gradeLevel?: number;
      university?: string;
      totalMatches: number;
    }>;
  };
  deposit: (amount: number) => Promise<void>;
  withdraw: (amount: number) => Promise<void>;
}

const CompetitionContext = createContext<CompetitionContextType | undefined>(undefined);

export const useCompetition = () => {
  const context = useContext(CompetitionContext);
  if (!context) {
    throw new Error('useCompetition must be used within a CompetitionProvider');
  }
  return context;
};

export const CompetitionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [currentCompetition, setCurrentCompetition] = useState<CompetitionContextType['currentCompetition']>(null);
  const [state, setState] = useState<CompetitionContextType['state']>({
    balance: 0,
    stats: {
      totalRounds: 0,
      wins: 0,
      losses: 0,
      draws: 0,
      rating: 0,
      yellowCards: 0,
      redCards: 0
    },
    availableCompetitors: []
  });

  const fetchCompetitionData = async () => {
    if (!user?.uid) return;

    try {
      const competitionRef = doc(db, 'competitions', 'current');
      const competitionDoc = await getDoc(competitionRef);

      if (competitionDoc.exists()) {
        const data = competitionDoc.data();
        setCurrentCompetition({
          id: competitionDoc.id,
          rewards: {
            available: data.rewards?.[user.uid]?.available || 0,
            withdrawn: data.rewards?.[user.uid]?.withdrawn || 0
          }
        });
      }
    } catch (error) {
      console.error('Error fetching competition data:', error);
    }
  };

  useEffect(() => {
    if (user?.uid) {
      fetchCompetitionData();
    }
  }, [fetchCompetitionData]);

  const getBalance = async (): Promise<number> => {
    try {
      if (!user?.uid) throw new Error('User not authenticated');
      const balanceDoc = await getDoc(doc(db, 'balances', user.uid));
      return balanceDoc.exists() ? balanceDoc.data().amount : 0;
    } catch (error) {
      console.error('Error fetching balance:', error);
      throw error;
    }
  };

  const getActiveChallenges = async (): Promise<any[]> => {
    try {
      if (!user?.uid) throw new Error('User not authenticated');
      const challengesRef = collection(db, 'challenges');
      const q = query(
        challengesRef,
        where('participants', 'array-contains', user.uid),
        where('status', '==', 'active')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({ 
        id: doc.id, 
        ...doc.data() 
      }));
    } catch (error) {
      console.error('Error fetching active challenges:', error);
      throw error;
    }
  };

  const getLeaderboard = async (): Promise<LeaderboardEntry[]> => {
    try {
      const leaderboardRef = collection(db, 'leaderboard');
      const q = query(leaderboardRef, orderBy('totalEarnings', 'desc'), limit(100));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>, index: number) => ({
        ...doc.data() as Omit<LeaderboardEntry, 'rank'>,
        rank: index + 1
      }));
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      throw error;
    }
  };

  const getChallengeResult = async (challengeId: string): Promise<ChallengeResult> => {
    try {
      const resultRef = doc(db, 'challengeResults', challengeId);
      const resultDoc = await getDoc(resultRef);
      
      if (!resultDoc.exists()) {
        throw new Error('Challenge result not found');
      }

      return resultDoc.data() as ChallengeResult;
    } catch (error) {
      console.error('Error fetching challenge result:', error);
      throw error;
    }
  };

  const subscribeToChallengeUpdates = (challengeId: string, callback: (data: any) => void) => {
    const resultRef = doc(db, 'challengeResults', challengeId);
    return onSnapshot(resultRef, (doc: DocumentSnapshot<DocumentData>) => {
      if (doc.exists()) {
        callback(doc.data());
      }
    });
  };

  const subscribeToLeaderboardUpdates = (callback: (data: LeaderboardEntry[]) => void) => {
    const leaderboardRef = collection(db, 'leaderboard');
    const q = query(leaderboardRef, orderBy('totalEarnings', 'desc'), limit(100));
    
    return onSnapshot(q, (snapshot: QuerySnapshot<DocumentData>) => {
      const leaderboardData = snapshot.docs.map((doc: QueryDocumentSnapshot<DocumentData>, index: number) => ({
        ...doc.data() as Omit<LeaderboardEntry, 'rank'>,
        rank: index + 1
      }));
      callback(leaderboardData);
    });
  };

  const withdrawReward = async (amount: number) => {
    if (!user || !currentCompetition) return;

    try {
      // Update local state first for optimistic UI
      setCurrentCompetition(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          rewards: {
            ...prev.rewards,
            available: prev.rewards.available - amount,
            withdrawn: prev.rewards.withdrawn + amount
          }
        };
      });

      // Get the current user's ID token
      const idToken = await user.getIdToken();

      // Call API to process withdrawal
      const response = await fetch('/api/competition/withdraw', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          competitionId: currentCompetition.id,
          amount
        })
      });

      if (!response.ok) {
        throw new Error('Failed to process withdrawal');
      }

      // Refresh competition data
      await fetchCompetitionData();
    } catch (error) {
      console.error('Withdrawal error:', error);
      throw error;
    }
  };

  const createCompetitionRound = async (round: Omit<CompetitionRound, 'id' | 'creatorId' | 'status' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.uid) throw new Error('User not authenticated');

    try {
      // Check if user has sufficient balance
      const balance = await getBalance();
      if (balance < round.stakeAmount) {
        throw new Error('Insufficient balance');
      }

      // Check if user has any active rounds
      const activeRoundsRef = collection(db, 'competitionRounds');
      const activeRoundsQuery = query(
        activeRoundsRef,
        where('creatorId', '==', user.uid),
        where('status', 'in', ['PENDING', 'ACTIVE'])
      );
      const activeRounds = await getDocs(activeRoundsQuery);

      if (!activeRounds.empty) {
        throw new Error('You already have an active competition round');
      }

      // Create new round
      const roundRef = doc(collection(db, 'competitionRounds'));
      const newRound: CompetitionRound = {
        id: roundRef.id,
        creatorId: user.uid,
        ...round,
        status: 'PENDING',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await setDoc(roundRef, newRound);

      // Deduct stake amount from user's balance
      const balanceRef = doc(db, 'balances', user.uid);
      await updateDoc(balanceRef, {
        amount: balance - round.stakeAmount
      });

      return roundRef.id;
    } catch (error) {
      console.error('Error creating competition round:', error);
      throw error;
    }
  };

  const acceptCompetitionRound = async (roundId: string) => {
    if (!user?.uid) throw new Error('User not authenticated');

    try {
      const roundRef = doc(db, 'competitionRounds', roundId);
      const roundDoc = await getDoc(roundRef);

      if (!roundDoc.exists()) {
        throw new Error('Competition round not found');
      }

      const round = roundDoc.data() as CompetitionRound;

      if (round.status !== 'PENDING') {
        throw new Error('Competition round is no longer available');
      }

      if (round.creatorId === user.uid) {
        throw new Error('Cannot accept your own challenge');
      }

      // Check if user has sufficient balance
      const balance = await getBalance();
      if (balance < round.stakeAmount) {
        throw new Error('Insufficient balance');
      }

      // Update round status
      await updateDoc(roundRef, {
        status: 'ACTIVE',
        opponentId: user.uid,
        updatedAt: new Date()
      });

      // Deduct stake amount from opponent's balance
      const balanceRef = doc(db, 'balances', user.uid);
      await updateDoc(balanceRef, {
        amount: balance - round.stakeAmount
      });

      // Notify creator
      // TODO: Implement notification system
    } catch (error) {
      console.error('Error accepting competition round:', error);
      throw error;
    }
  };

  const cancelCompetitionRound = async (roundId: string, reason: string) => {
    if (!user?.uid) throw new Error('User not authenticated');

    try {
      const roundRef = doc(db, 'competitionRounds', roundId);
      const roundDoc = await getDoc(roundRef);

      if (!roundDoc.exists()) {
        throw new Error('Competition round not found');
      }

      const round = roundDoc.data() as CompetitionRound;

      if (round.creatorId !== user.uid) {
        throw new Error('Only the creator can cancel the round');
      }

      if (round.status !== 'PENDING') {
        throw new Error('Cannot cancel an active or completed round');
      }

      // Validate reason
      const validReasons = [
        'electricity_blackout',
        'sudden_sickness',
        'floods',
        'intensive_rainfall',
        'crowded_place',
        'nightfall',
        'data_expiry',
        'connection_issues',
        'natural_calamities',
        'government_reasons',
        'no_devices'
      ];

      // TODO: Implement reason validation and translation
      const isValidReason = true; // Placeholder

      if (!isValidReason) {
        // Issue yellow card
        const userStatsRef = doc(db, 'userStats', user.uid);
        await updateDoc(userStatsRef, {
          yellowCards: arrayUnion(new Date()),
          cardExpiry: Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000)) // 24 hours
        });
      }

      // Update round status
      await updateDoc(roundRef, {
        status: 'CANCELLED',
        updatedAt: new Date()
      });

      // Refund stake amount
      const balanceRef = doc(db, 'balances', user.uid);
      const balanceDoc = await getDoc(balanceRef);
      const currentBalance = balanceDoc.exists() ? balanceDoc.data().amount : 0;

      await updateDoc(balanceRef, {
        amount: currentBalance + round.stakeAmount
      });
    } catch (error) {
      console.error('Error cancelling competition round:', error);
      throw error;
    }
  };

  const getUserStats = async (userId: string): Promise<UserStats> => {
    try {
      const statsRef = doc(db, 'userStats', userId);
      const statsDoc = await getDoc(statsRef);

      if (!statsDoc.exists()) {
        return {
          totalRounds: 0,
          wins: 0,
          losses: 0,
          draws: 0,
          rating: 0,
          yellowCards: 0,
          redCards: 0
        };
      }

      const stats = statsDoc.data() as UserStats;
      return stats;
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  };

  const getAvailableCompetitors = async (page: number) => {
    try {
      const roundsRef = collection(db, 'competitionRounds');
      const q = query(
        roundsRef,
        where('status', '==', 'PENDING'),
        orderBy('startTime', 'asc'),
        limit(20 * page)
      );

      const snapshot = await getDocs(q);
      const competitors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      return {
        data: competitors,
        hasMore: competitors.length === 20 * page
      };
    } catch (error) {
      console.error('Error fetching available competitors:', error);
      throw error;
    }
  };

  const deposit = async (amount: number) => {
    if (!user?.uid) throw new Error('User not authenticated');

    try {
      const balanceRef = doc(db, 'balances', user.uid);
      const balanceDoc = await getDoc(balanceRef);
      const currentBalance = balanceDoc.exists() ? balanceDoc.data().amount : 0;

      await updateDoc(balanceRef, {
        amount: currentBalance + amount
      });

      setState(prev => ({
        ...prev,
        balance: prev.balance + amount
      }));
    } catch (error) {
      console.error('Error depositing funds:', error);
      throw error;
    }
  };

  const withdraw = async (amount: number) => {
    if (!user?.uid) throw new Error('User not authenticated');

    try {
      const balanceRef = doc(db, 'balances', user.uid);
      const balanceDoc = await getDoc(balanceRef);
      const currentBalance = balanceDoc.exists() ? balanceDoc.data().amount : 0;

      if (currentBalance < amount) {
        throw new Error('Insufficient balance');
      }

      await updateDoc(balanceRef, {
        amount: currentBalance - amount
      });

      setState(prev => ({
        ...prev,
        balance: prev.balance - amount
      }));
    } catch (error) {
      console.error('Error withdrawing funds:', error);
      throw error;
    }
  };

  const value = {
    getBalance,
    getActiveChallenges,
    getLeaderboard,
    getChallengeResult,
    subscribeToChallengeUpdates,
    subscribeToLeaderboardUpdates,
    withdrawReward,
    createCompetitionRound,
    acceptCompetitionRound,
    cancelCompetitionRound,
    getUserStats,
    getAvailableCompetitors,
    currentCompetition,
    state,
    deposit,
    withdraw
  };

  return (
    <CompetitionContext.Provider value={value}>
      {children}
    </CompetitionContext.Provider>
  );
}; 